import { useState, forwardRef } from "react";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import { useRecoilValue } from "recoil";
import styled from "styled-components";

import { FilterPillBar } from "../FilterPillBar";
import { Thumbnails } from "../Thumbnails";
import { WatchLimitBlocker } from "../WatchLimitBlocker";
import { EmbeddablePlaylistViewModel, TagItem } from "../../ViewModel";
import EmbeddablePlayer from "../../../EmbeddablePlayer";
import { NoClips } from "../NoClips";
import { Button } from "MVVM/Components/Button";
import { LoadingSkeleton } from "MVVM/Components/Skeloton";
import {
  PlaylistClipsState,
  ActivePlaylistClipState,
  PlayerPhotoState,
  TeamLogoState,
  FallbackPlaylistTypeState,
  ShowWatchLimitBlockerState,
  BlockedPlayerNameState,
  BlockedClipState,
  RenderPreMatchState,
  TopViewedPlayersState,
  TopViewedMatchesState,
  TopViewedTeamsState,
  DisplayEmptyState,
  TopViewedPlayersByCountryState,
} from "MVVM/State/embeddablePlaylist";
import { useWatchLimit } from "MVVM/Hooks/useWatchLimit";
import { TopViewedListContainer } from "../TopViewedListContainer";
import { EmptyState } from "./Components/EmptyState";

export const HLTVEmbeddablePlaylist = forwardRef<
  HTMLDivElement,
  {
    platform: string;
    playlist: string;
    theme?: string;
    handleMouseEnter: () => void;
  }
>(({ platform, playlist, theme = "night", handleMouseEnter }, ref) => {
  const [selectedTag, setSelectedTag] = useState<string>("all");
  const showWatchLimitBlocker = useRecoilValue(ShowWatchLimitBlockerState);
  const blockedPlayerName = useRecoilValue(BlockedPlayerNameState);
  const blockedClip = useRecoilValue(BlockedClipState);
  const playlistClips = useRecoilValue(PlaylistClipsState);
  const activePlaylistClip = useRecoilValue(ActivePlaylistClipState);
  const playerPhoto = useRecoilValue(PlayerPhotoState);
  const teamLogo = useRecoilValue(TeamLogoState);
  const displayEmptyState = useRecoilValue(DisplayEmptyState);
  const fallbackPlaylistType = useRecoilValue(FallbackPlaylistTypeState);
  const renderPreMatchState = useRecoilValue(RenderPreMatchState);
  const topViewedPlayers = useRecoilValue(TopViewedPlayersState);
  const topViewedPlayersByCountry = useRecoilValue(
    TopViewedPlayersByCountryState,
  );
  const topViewedMatches = useRecoilValue(TopViewedMatchesState);
  const topViewedTeams = useRecoilValue(TopViewedTeamsState);
  const renderTopViewedComponent = !!topViewedPlayers && !!topViewedMatches;
  const renderTopViewedList = new URLSearchParams(window.location.search).get(
    "topViewedList",
  );

  const { handleWatchLimitReached, handleCloseBlocker } = useWatchLimit();

  const { filters, getClipsForTag } = EmbeddablePlaylistViewModel({
    playlist,
    platform,
  });

  const filterPills = filters.map((tag: TagItem) => ({
    label: tag.value,
    key: `${tag.key}:${tag.value}`,
    count: tag.clips.length,
  }));

  const allFilters = [
    { label: "All", key: "all", count: playlistClips?.length || 0 },
    ...filterPills,
    { label: "More", key: "more", count: 0, externalLink: true },
  ];

  const clipsToDisplay = getClipsForTag(selectedTag);

  if (displayEmptyState)
    return (
      <Flex flexDirection="column" width="100%">
        <StyledContainer
          $renderTopViewedComponent={true}
          $renderPreMatchState={renderPreMatchState}
        >
          <StyledGradientLayer
            className="gradient-layer"
            $renderTopViewedComponent={true}
          >
            <EmptyState />
          </StyledGradientLayer>
          {!topViewedPlayers || !topViewedMatches || !topViewedTeams ? (
            <Box style={{ height: "150px" }}>
              <LoadingSkeleton height="100%" width="100%" />
            </Box>
          ) : (
            <TopViewedListContainer
              topViewedPlayers={topViewedPlayers}
              topViewedPlayersByCountry={topViewedPlayersByCountry}
              topViewedMatches={topViewedMatches}
              topViewedTeams={topViewedTeams}
              theme={theme}
            />
          )}
        </StyledContainer>
      </Flex>
    );
  return (
    <Flex flexDirection={"column"} style={{ height: "100%", width: "100%" }}>
      <StyledContainer
        $renderTopViewedComponent={renderTopViewedComponent}
        $renderPreMatchState={renderPreMatchState}
        onMouseEnter={handleMouseEnter}
      >
        <StyledGradientLayer
          className="gradient-layer"
          $renderTopViewedComponent={false}
        >
          <StyledWrapper $renderPreMatchState={renderPreMatchState}>
            <FixedPlayerWrapper
              ref={ref}
              className="fixed-width-container"
              $renderPreMatchState={renderPreMatchState}
            >
              {activePlaylistClip && (
                <EmbeddablePlayer
                  userRole="viewer"
                  onPlayEndVariant="nextUpPlaylist"
                />
              )}
              {fallbackPlaylistType &&
                !["lastMatch", "teamClips"].includes(fallbackPlaylistType) && (
                  <NoClips />
                )}
              {!activePlaylistClip && !fallbackPlaylistType && (
                <PlayerLoadingSkeleton>
                  <LoadingSkeleton width="100%" height="100%" />
                </PlayerLoadingSkeleton>
              )}
              {showWatchLimitBlocker && (
                <WatchLimitBlocker
                  onClose={handleCloseBlocker}
                  playerName={blockedPlayerName}
                  clickedClip={blockedClip}
                  primaryColor="#4DBD94"
                  playlistId={playlist}
                />
              )}
            </FixedPlayerWrapper>
            {renderPreMatchState ? (
              <StyledPreMatchContainer>
                <StyledLogo
                  src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/allstarLogoGradient.svg`}
                />
                <StyledPreMatchText>
                  See your own clips while waiting for this match.
                </StyledPreMatchText>
                <StyledPreMatchButton
                  onClick={() => {
                    const platform = new URLSearchParams(
                      window.location.search,
                    ).get("platform");
                    const ajs_aid = window.rudderanalytics.getAnonymousId();
                    window.open(
                      `https://allstar.gg/match-history?ajs_aid=${ajs_aid}&utm_source=${platform}`,
                      "_blank",
                    );
                  }}
                >
                  Click Here
                </StyledPreMatchButton>
              </StyledPreMatchContainer>
            ) : (
              <StyledImageContainer>
                {teamLogo && <StyledTeamImage src={teamLogo} />}
                {playerPhoto && <StyledPlayerImage src={playerPhoto} />}
              </StyledImageContainer>
            )}
          </StyledWrapper>
          {renderPreMatchState ? (
            !topViewedPlayers || !topViewedMatches || !topViewedTeams ? (
              <StyledLoadingContainer>
                <LoadingSkeleton height="100%" width="100%" />
              </StyledLoadingContainer>
            ) : (
              <TopViewedListContainer
                topViewedPlayers={topViewedPlayers}
                topViewedPlayersByCountry={topViewedPlayersByCountry}
                topViewedMatches={topViewedMatches}
                topViewedTeams={topViewedTeams}
                theme={theme}
              />
            )
          ) : (
            <>
              {allFilters.length > 0 ? (
                <FilterPillBar
                  filters={allFilters}
                  selected={selectedTag}
                  onSelect={setSelectedTag}
                />
              ) : (
                <FilterPillBarSkeleton>
                  <Flex style={{ gap: "8px" }}>
                    {Array.from({ length: 5 }, (_, i) => (
                      <LoadingSkeleton
                        key={i}
                        width={48}
                        height={22}
                        style={{ borderRadius: "20px" }}
                      />
                    ))}
                  </Flex>
                </FilterPillBarSkeleton>
              )}
              <Thumbnails
                clipsToDisplay={clipsToDisplay}
                onWatchLimitReached={handleWatchLimitReached}
              />
            </>
          )}
          {renderTopViewedList && !renderPreMatchState && (
            <StyledTopViewedListWrapper>
              {!topViewedPlayers || !topViewedMatches || !topViewedTeams ? (
                <StyledLoadingContainer>
                  <LoadingSkeleton height="100%" width="100%" />
                </StyledLoadingContainer>
              ) : (
                <TopViewedListContainer
                  topViewedPlayers={topViewedPlayers}
                  topViewedPlayersByCountry={topViewedPlayersByCountry}
                  topViewedMatches={topViewedMatches}
                  topViewedTeams={topViewedTeams}
                  theme={theme}
                />
              )}
            </StyledTopViewedListWrapper>
          )}
        </StyledGradientLayer>
      </StyledContainer>
    </Flex>
  );
});

const StyledTopViewedListWrapper = styled(Flex)`
  min-height: 160px;

  @media (max-width: 360px) {
    min-height: 130px;
  }
`;

const StyledWrapper = styled(Box)<{ $renderPreMatchState: boolean }>`
  ${({ $renderPreMatchState }) =>
    $renderPreMatchState
      ? `
      align-items: center;
      display: flex;
      flex-direction: row;
      gap: 12px;
      min-height: 235px;
      width: 100%;

      @media (max-width: 502px) {
        min-height: 162px;
      }

      @media (max-width: 380px) {
        min-height: 112px;
      }
      `
      : `
      min-height: 288px;
      position: relative;

      @media (max-width: 502px) {
        min-height: 210px;
      }

      @media (max-width: 425px) {
        min-height: 185px;
      }

      @media (max-width: 380px) {
        min-height: 162px;
      }
      `}
`;

const StyledTeamImage = styled(Image)`
  position: absolute;
  opacity: 75%;
  right: -24px;
  top: 24px;
  width: 150px;

  @media (max-width: 600px) {
    right: -35px;
  }

  @media (max-width: 502px) {
    right: -14px;
    width: 128px;
    top: 0px;
  }

  @media (max-width: 425px) {
    right: -30px;
  }

  @media (max-width: 380px) {
    right: -18px;
    width: 96px;
  }
`;

const StyledPlayerImage = styled(Image)`
  position: absolute;
  right: -22px;
  top: 88px;
  width: 150px;

  -webkit-mask-image: linear-gradient(to bottom, black 70%, transparent 100%);
  mask-image: linear-gradient(to bottom, black 70%, transparent 100%);
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;

  @media (max-width: 600px) {
    right: -35px;
  }

  @media (max-width: 502px) {
    width: 160px;
    right: -30px;
    top: 48px;
  }

  @media (max-width: 425px) {
    right: -45px;
    top: 50px;
  }

  @media (max-width: 380px) {
    right: -34px;
    top: 45px;
    width: 125px;
  }
`;

const StyledImageContainer = styled(Box)`
  position: relative;
`;

const FixedPlayerWrapper = styled(Box)<{ $renderPreMatchState: boolean }>`
  ${({ $renderPreMatchState }) =>
    $renderPreMatchState
      ? `
        flex-shrink: 0;
        height: 235px;
        position: relative;
        width: 416px;
        z-index: 1;
        
        .player-component {
          position: relative;
        }

        &:not(:has(:fullscreen)) {
          .vjs-tech {
            border-radius: 8px;
            height: auto !important;
            width: 100% !important;
          }
          .video-js {
            background: transparent;
            width: 100% !important;
            height: 100% !important;
            min-height: 235px;
          }
        }

        @media (max-width: 502px) {
          .embeddable-player {
            .video-js {
              min-height: 162px;
            }
          }
        }

        @media (max-width: 380px) {
          .embeddable-player {
            .video-js {
              min-height: 112px;
            }
          }
        }
      `
      : `
        bottom: 0;
        height: 288px;
        left: 0;
        position: absolute;
        top: 0;
        width: 512px;
        z-index: 1;

        &:not(:has(:fullscreen)) {
          .vjs-tech {
            border-radius: 8px;
            height: auto !important;
            width: 100% !important;
          }
          .video-js {
            background: transparent;
            width: 100% !important;
            height: 100% !important;
            min-height: 288px;
          }
        }

        @media (max-width: 502px) {
          .embeddable-player {
            .video-js {
              min-height: 207px;
            }
          }
        }

        @media (max-width: 425px) {
          .embeddable-player {
            .video-js {
              min-height: 185px;
            }
          }
        }

        @media (max-width: 380px) {
          .embeddable-player {
            .video-js {
              min-height: 162px;
            }
          }
        }
  `}
`;

const FilterPillBarSkeleton = styled(Box)``;

const PlayerLoadingSkeleton = styled(Box)`
  border-radius: 8px;
  height: 100%;
  width: 100%;
`;

const StyledLogo = styled(Image)`
  height: 60px;
  width: 34px;

  @media (max-width: 502px) {
    height: 43px;
    width: 24px;
  }

  @media (max-width: 380px) {
    height: 32px;
    width: 18px;
  }
`;

const StyledPreMatchText = styled(Text)`
  font-size: 18px;
  font-weight: 700;
  line-height: 22px;
  text-align: center;

  @media (max-width: 502px) {
    font-size: 13px;
    line-height: 15px;
  }

  @media (max-width: 380px) {
    font-size: 10px;
    line-height: 12px;
  }
`;

const StyledPreMatchContainer = styled(Flex)`
  align-items: center;
  flex-direction: column;
  gap: 20px;
  justify-content: center;
  margin: 0 auto;
  width: 200px;

  @media (max-width: 502px) {
    gap: 10px;
  }
`;

const StyledPreMatchButton = styled(Button)`
  background: linear-gradient(225deg, #f95370 0%, #72439a 100%);
  border-radius: 100px;
  font-size: 15px;
  font-weight: 700;
  height: 2.5rem;
  padding: 0px;
  line-height: 2.5rem;
  width: 115px;

  @media (max-width: 502px) {
    font-size: 10px;
    height: 1.5rem;
    line-height: 1.5rem;
  }

  @media (max-width: 380px) {
    font-size: 8px;
    height: 1rem;
    line-height: 1rem;
    width: 60px;
  }
`;

const StyledLoadingContainer = styled(Box)`
  height: 180px;

  @media (max-width: 502px) {
    height: 150px;
  }

  @media (max-width: 380px) {
    height: 120px;
  }
`;
const StyledGradientLayer = styled(Flex)<{
  $renderTopViewedComponent: boolean;
}>`
  backdrop-filter: blur(6px);
  background: linear-gradient(
      227.35deg,
      rgba(49, 113, 138, 0.3) -3.85%,
      rgba(99, 173, 146, 0.3) 100%
    ),
    linear-gradient(0deg, rgba(0, 0, 0, 0.87), rgba(0, 0, 0, 0.87));
  flex-direction: column;
  gap: 12px;
  height: 100%;
  padding: 10px;
  width: 100%;

  ${({ $renderTopViewedComponent }) =>
    $renderTopViewedComponent &&
    `
    flex: 1;
  `}

  @media (max-width: 502px) {
    gap: 8px;
    padding: 8px;
  }

  @media (max-width: 425px) {
    padding: 6px;
  }
`;

const StyledContainer = styled(Flex)<{
  $renderTopViewedComponent: boolean;
  $renderPreMatchState: boolean;
}>`
  background-image: url(${process.env
    .REACT_APP_S3_CLIP_BUCKET}/static/web/playlistBackgroundImage.jpg);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  ${({ $renderTopViewedComponent }) =>
    $renderTopViewedComponent &&
    `
      flex: 1;
      min-height: 0;
      gap: 0px;
    `}

  ${({ $renderTopViewedComponent }) =>
    !$renderTopViewedComponent &&
    `
        height: 100%;
      `}

  ${({ $renderPreMatchState }) =>
    $renderPreMatchState
      ? `
      @media (max-width: 502px) {
        .fixed-width-container {
          height: 162px;
          width: 288px;
        }
      }

      @media (max-width: 380px) {
        .fixed-width-container {
          height: 112px;
          width: 200px;
        }
      }
      `
      : `
          @media (max-width: 502px) {
            .fixed-width-container {
              height: 207px;
              width: 368px;
            }
          }

          @media (max-width: 425px) {
            .fixed-width-container {
              height: 185px;
              width: 330px;
            }
          }

          @media (max-width: 380px) {
            .fixed-width-container {
              height: 162px;
              width: 288px;
            }
          }
        `}
`;
